import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { Stethoscope, Users, Siren, Hash, AlertCircle } from 'lucide-react';
import { dashboardApi } from '@/lib/api';
import type { DoctorDashboardData, OpdToken, VitalFlag } from './types';
import {
  MetricCard, MetricsGrid, SectionCard, TokenStatusBadge, GenericBadge,
  DashboardShell, ScrollTable, Th, Td, LoadingState, ErrorState,
} from './_shared';

const VISIT_TYPE_MAP: Record<OpdToken['visitType'], { label: string; cls: string }> = {
  new:        { label: 'New',        cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  follow_up:  { label: 'Follow-up',  cls: 'bg-gray-100 text-gray-500 border-gray-200' },
  lab_review: { label: 'Lab Review', cls: 'bg-violet-50 text-violet-700 border-violet-200' },
  emergency:  { label: 'Emergency',  cls: 'bg-red-100 text-red-700 border-red-200' },
};

function VitalFlags({ flags }: { flags: VitalFlag[] }) {
  if (!flags.length) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {flags.map(f => (
        <span
          key={f.parameter}
          className={clsx(
            'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-2xs font-semibold border',
            f.severity === 'critical'
              ? 'bg-red-50 text-red-700 border-red-200'
              : 'bg-amber-50 text-amber-700 border-amber-200',
          )}>
          <AlertCircle size={9}/>
          {f.parameter}: {f.value} {f.unit}
        </span>
      ))}
    </div>
  );
}

// ── Component ───────────────────────────────────────────────────────────────
export default function DoctorDashboard() {
  const { data, isLoading, isError, refetch, isFetching } = useQuery<DoctorDashboardData>({
    queryKey:        ['dashboard-doctor'],
    queryFn:         () => dashboardApi.doctor().then(r => r.data),
    staleTime:       60_000,
    refetchInterval: 2 * 60_000,
  });

  if (isLoading) return <LoadingState/>;
  if (isError || !data) return <ErrorState onRetry={() => refetch()}/>;

  return (
    <DashboardShell
      title="OPD Queue"
      subtitle="Today's token list with vital alerts"
      onRefresh={() => refetch()}
      refreshing={isFetching}>

      <MetricsGrid>
        <MetricCard icon={Stethoscope} label="Remaining Appointments" value={data.remainingAppointments} accent="bg-orange/15"/>
        <MetricCard icon={Users}       label="Seen Today"             value={data.seenToday}             accent="bg-emerald-50"/>
        <MetricCard icon={Siren}       label="Emergency Consults"     value={data.emergencyConsults}      accent="bg-red-50" alert={data.emergencyConsults > 0}/>
        <MetricCard icon={Hash}        label="Flags Detected"         value={data.tokens.reduce((n, t) => n + t.vitalFlags.length, 0)} accent="bg-amber-50" alert/>
      </MetricsGrid>

      <SectionCard title={`Token Queue (${data.tokens.length})`}>
        <ScrollTable>
          <thead>
            <tr>
              <Th>#</Th><Th>Patient</Th><Th>Age/Sex</Th><Th>Visit</Th>
              <Th>Status</Th><Th>Time</Th><Th>Chief Complaint + Vital Alerts</Th>
            </tr>
          </thead>
          <tbody>
            {data.tokens.map(t => (
              <tr key={t.tokenNumber} className={clsx(
                'hover:bg-gray-50 transition',
                t.status === 'in_progress' && 'bg-blue-50/30',
                t.visitType === 'emergency' && 'bg-red-50/30',
              )}>
                <Td>
                  <span className="w-8 h-8 rounded-full bg-navy text-white text-xs font-bold flex items-center justify-center">
                    {t.tokenNumber}
                  </span>
                </Td>
                <Td><span className="font-medium">{t.patientName}</span></Td>
                <Td muted>{t.age}y / {t.gender}</Td>
                <Td>
                  <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-2xs font-semibold border', VISIT_TYPE_MAP[t.visitType].cls)}>
                    {VISIT_TYPE_MAP[t.visitType].label}
                  </span>
                </Td>
                <Td><TokenStatusBadge status={t.status}/></Td>
                <Td muted>{t.scheduledTime}</Td>
                <Td>
                  <p className="text-gray-700 text-xs">{t.chiefComplaint}</p>
                  <VitalFlags flags={t.vitalFlags}/>
                </Td>
              </tr>
            ))}
          </tbody>
        </ScrollTable>
      </SectionCard>

    </DashboardShell>
  );
}
